import { useState, useEffect, useCallback } from 'react'
import { PenLine, Plus, Trash2, Star } from 'lucide-react'
import { useAuth } from '@/shared/contexts/AuthContext'
import { SignaturePad } from './SignaturePad'
import {
  getStoredSignatures,
  saveSignature,
  deleteSignature,
  setDefaultSignature,
  type StoredSignature,
} from '../services/signatureStorage'
import toast from 'react-hot-toast'

export function MySignatures() {
  const { user } = useAuth()
  const [signatures, setSignatures] = useState<StoredSignature[]>([])
  const [loading, setLoading] = useState(true)
  const [showPad, setShowPad] = useState(false)

  const loadSignatures = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getStoredSignatures()
      setSignatures(data)
    } catch (err) {
      console.error('Error loading signatures:', err)
      toast.error('Error al cargar firmas')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSignatures()
  }, [loadSignatures])

  const handleSave = async (imageData: string, type: 'draw' | 'upload') => {
    if (!user) return
    try {
      await saveSignature({
        userId: user.id,
        name: `Firma ${signatures.length + 1}`,
        signatureType: type,
        imageData,
        isDefault: signatures.length === 0,
      })
      toast.success('Firma guardada')
      setShowPad(false)
      loadSignatures()
    } catch (err) {
      console.error('Error saving signature:', err)
      toast.error('Error al guardar firma')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminar esta firma?')) return
    try {
      await deleteSignature(id)
      toast.success('Firma eliminada')
      loadSignatures()
    } catch (err) {
      console.error('Error deleting:', err)
      toast.error('Error al eliminar')
    }
  }

  const handleSetDefault = async (id: string) => {
    if (!user) return
    try {
      await setDefaultSignature(id, user.id)
      toast.success('Firma predeterminada actualizada')
      loadSignatures()
    } catch (err) {
      console.error('Error setting default:', err)
      toast.error('Error al actualizar')
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-legal-900 dark:text-white flex items-center gap-3">
            <PenLine className="text-primary-600 dark:text-primary-400" size={28} />
            Mis Firmas
          </h1>
          <p className="text-sm text-legal-500 dark:text-legal-400 mt-1">
            Gestiona tus firmas para usarlas al firmar documentos
          </p>
        </div>
        {!showPad && (
          <button
            onClick={() => setShowPad(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            <Plus size={18} />
            Nueva Firma
          </button>
        )}
      </div>

      {showPad && (
        <div className="mb-6">
          <SignaturePad
            onSave={handleSave}
            onCancel={() => setShowPad(false)}
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-legal-500 dark:text-legal-400">
          Cargando firmas...
        </div>
      ) : signatures.length === 0 && !showPad ? (
        <div className="text-center py-16 bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700">
          <PenLine size={48} className="mx-auto text-legal-300 dark:text-legal-600 mb-4" />
          <p className="text-legal-600 dark:text-legal-300 mb-2">No tenes firmas guardadas</p>
          <p className="text-sm text-legal-400 dark:text-legal-500 mb-4">
            Crea tu primera firma para poder firmar documentos
          </p>
          <button
            onClick={() => setShowPad(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
          >
            Crear Firma
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {signatures.map((sig) => (
            <div
              key={sig.id}
              className={`bg-white dark:bg-legal-800 rounded-xl border p-4 ${
                sig.is_default
                  ? 'border-primary-300 dark:border-primary-600'
                  : 'border-legal-200 dark:border-legal-700'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-legal-700 dark:text-legal-200">
                    {sig.name}
                  </span>
                  {sig.is_default && (
                    <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">
                      Predeterminada
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  {!sig.is_default && (
                    <button
                      onClick={() => handleSetDefault(sig.id)}
                      title="Establecer como predeterminada"
                      className="p-1.5 text-legal-400 hover:text-primary-600 dark:hover:text-primary-400 rounded"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(sig.id)}
                    title="Eliminar"
                    className="p-1.5 text-legal-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="bg-legal-50 dark:bg-legal-900 rounded-lg p-3 flex items-center justify-center">
                <img
                  src={sig.image_data}
                  alt={sig.name}
                  className="max-h-20 object-contain"
                />
              </div>
              <p className="text-xs text-legal-400 dark:text-legal-500 mt-2">
                {sig.signature_type === 'draw' ? 'Dibujada' : 'Subida'} -{' '}
                {new Date(sig.created_at).toLocaleDateString('es-AR')}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
